import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Copy, Loader2 } from 'lucide-react';
import BuildQuoteCard from '../components/Build/BuildQuoteCard';
import type { BuildIntake, BuildQuoteResult } from '../lib/buildQuote/types';
import { isConsultationProjectType } from '../lib/buildQuote/types';
import {
  fetchSavedQuote,
  negotiateSavedQuote,
  openBuildCheckout,
  createBuildCheckoutSession,
  parseConversationNotes,
} from '../lib/buildQuote/api';
import type { BuildPaymentType } from '../lib/buildQuote/api';
import { formatUsd } from '../lib/buildQuote/format';

export default function BuildQuotePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('checkout') === 'cancelled';

  const [intake, setIntake] = useState<BuildIntake | null>(null);
  const [quote, setQuote] = useState<BuildQuoteResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<BuildPaymentType | null>(null);
  const [linkLoading, setLinkLoading] = useState<BuildPaymentType | null>(null);
  const [negotiating, setNegotiating] = useState(false);
  const [clientMessage, setClientMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'quote' | BuildPaymentType | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchSavedQuote(id)
      .then((data) => {
        setIntake(data.intake);
        setQuote(data.quote);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCheckout = async (paymentType: BuildPaymentType) => {
    if (!id) return;
    setCheckoutLoading(paymentType);
    setError(null);
    try {
      await openBuildCheckout(id, paymentType);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setCheckoutLoading(null);
    }
  };

  const copyPaymentLink = async (paymentType: BuildPaymentType) => {
    if (!id) return;
    setLinkLoading(paymentType);
    setError(null);
    try {
      const session = await createBuildCheckoutSession(id, paymentType, {
        linkOnly: true,
      });
      await navigator.clipboard.writeText(session.url);
      setCopied(paymentType);
      setTimeout(() => setCopied(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create payment link');
    } finally {
      setLinkLoading(null);
    }
  };

  const copyQuoteLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied('quote');
    setTimeout(() => setCopied(null), 2500);
  };

  const handleNegotiate = async () => {
    if (!id) return;
    setNegotiating(true);
    setError(null);
    try {
      const result = await negotiateSavedQuote(id, {
        clientMessage: clientMessage.trim() || undefined,
        conversationHistory: parseConversationNotes(intake.conversationNotes || ''),
      });
      setQuote(result.quote);
      setClientMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revise quote');
    } finally {
      setNegotiating(false);
    }
  };

  const copyLink = copyQuoteLink;

  const consultationOnly =
    (quote != null && isConsultationProjectType(quote.projectType)) ||
    (intake != null && isConsultationProjectType(intake.projectType));

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black text-white">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (!quote || !intake || !id) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-black px-4 text-white">
        <p className="text-white/60">{error ?? 'Quote not found'}</p>
        <Link to="/build" className="text-white underline">
          Start a new quote
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your project quote — SELF Labs</title>
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%)',
          }}
        />

        <div className="relative mx-auto max-w-2xl px-4 pb-20 pt-28">
          <Link
            to={intake.projectType === 'consultation' ? '/consultation' : '/build'}
            className="inline-flex items-center text-sm text-white/50 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {intake.projectType === 'consultation' ? 'Back to consultation' : 'New quote'}
          </Link>

          <h1 className="mt-8 text-3xl font-bold">
            {intake.projectName || intake.company || 'Your project quote'}
          </h1>
          <p className="mt-2 text-white/55">
            For {intake.contactName} · {intake.email}
          </p>

          {cancelled && (
            <p className="mt-4 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/60">
              Checkout was cancelled. Your quote is still saved below.
            </p>
          )}

          <div className="mt-8">
            <BuildQuoteCard quote={quote} />
          </div>

          {error && (
            <p className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {!consultationOnly && (
              <button
                type="button"
                onClick={() => handleCheckout('deposit')}
                disabled={checkoutLoading !== null || quote.depositCents < 5000}
                className="flex-1 rounded-xl border border-white/20 bg-white/5 py-3 font-semibold hover:bg-white/10 disabled:opacity-50"
                title={
                  quote.depositCents < 5000
                    ? 'Deposit is below Stripe’s $50 minimum — pay in full instead'
                    : undefined
                }
              >
                {checkoutLoading === 'deposit' ? (
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                ) : quote.depositCents < 5000 ? (
                  'Deposit unavailable (under $50)'
                ) : (
                  `Pay deposit ${formatUsd(quote.depositCents)}`
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleCheckout('full')}
              disabled={checkoutLoading !== null}
              className={`flex-1 rounded-xl py-3 font-semibold disabled:opacity-50 ${
                consultationOnly
                  ? 'bg-white text-black hover:bg-white/90'
                  : 'border border-white/20 bg-white/5 hover:bg-white/10'
              }`}
            >
              {checkoutLoading === 'full' ? (
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              ) : (
                `Pay ${formatUsd(quote.currentTotalCents)}`
              )}
            </button>
          </div>

          <div
            className={`mt-3 grid gap-2 ${consultationOnly ? 'sm:grid-cols-1' : 'sm:grid-cols-2'}`}
          >
            {!consultationOnly && (
              <button
                type="button"
                onClick={() => copyPaymentLink('deposit')}
                disabled={linkLoading !== null}
                className="flex items-center justify-center gap-2 rounded-xl border border-white/15 py-2.5 text-xs text-white/65 hover:border-white/30 disabled:opacity-50"
              >
                {linkLoading === 'deposit' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copied === 'deposit' ? 'Deposit link copied!' : 'Copy deposit payment link'}
              </button>
            )}
            <button
              type="button"
              onClick={() => copyPaymentLink('full')}
              disabled={linkLoading !== null}
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 py-2.5 text-xs text-white/65 hover:border-white/30 disabled:opacity-50"
            >
              {linkLoading === 'full' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {copied === 'full' ? 'Payment link copied!' : 'Copy payment link'}
            </button>
          </div>

          {!consultationOnly && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <input
              type="text"
              value={clientMessage}
              onChange={(e) => setClientMessage(e.target.value)}
              placeholder="What did the client say about pricing? (optional)"
              className="flex-1 rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/35"
            />
            <button
              type="button"
              onClick={handleNegotiate}
              disabled={negotiating || quote.discountPercent >= quote.maxDiscountPercent}
              className="flex-1 rounded-xl border border-white/15 py-3 text-sm text-white/70 hover:border-white/30 disabled:opacity-40"
            >
              {negotiating
                ? 'Updating…'
                : quote.discountPercent >= quote.maxDiscountPercent
                  ? 'Best rate applied'
                  : 'Request revised pricing'}
            </button>
            <button
              type="button"
              onClick={copyLink}
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm text-white/70 hover:border-white/30"
            >
              <Copy className="h-4 w-4" />
              {copied === 'quote' ? 'Copied!' : 'Copy quote link'}
            </button>
          </div>
          )}

          {consultationOnly && (
            <button
              type="button"
              onClick={copyLink}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-sm text-white/70 hover:border-white/30"
            >
              <Copy className="h-4 w-4" />
              {copied === 'quote' ? 'Copied!' : 'Copy booking link'}
            </button>
          )}

          <p className="mt-6 text-center text-xs text-white/40">
            Quote ID: {id}. Team follows up within 2 business days after payment.
          </p>
        </div>
      </div>
    </>
  );
}
